import moment from 'moment';
import { round, cloneDeep } from 'lodash';
import { formatTimeToStr, formatDateToInt } from 'src/utils';
import hasOB from './hasOB';
import { PAID_LEAVES, solveExpression } from '../utils';
import { HOLIDAY_TYPES, PR_MODES, PR_VARIABLES_DEFAULT } from 'src/constants';
import calculateNightDifferential from './calculateNightDifferential';
import convertMinsToHours from './convertMinsToHours';
import getSchedule from './getSchedule';
import { isString } from 'lodash';

/**
 *
 * @param {computeTimeLogsParam} props
 * @returns {computeTimeLogsReturn}
 */
function computeTimeLogs({
  date,
  workHours = { am_in: '', am_out: '', pm_in: '', pm_out: '' },
  shifts = { shf_in1: '', shf_out1: '', shf_in2: '', shf_out2: '' },
  logs: { in1, out1, in2, out2, otIn, otOut },
  holidays = [],
  leaves = [],
  isRestDay = false,
  policy,
  nightDiff: { start: ndStart, end: ndEnd },
  prMode = 0,
  bioExempt = false,
  variables = {}
}) {
  const dailyMaxRegMins = policy.dailyMaxRegHrs * 60;

  let nightDiffHrs = 0,
    minsRenderedOnRestDay = dailyMaxRegMins;

  const vars = cloneDeep(PR_VARIABLES_DEFAULT);

  const paidLeave = leaves.find(leave => PAID_LEAVES.includes(leave.ixSILType));

  if (paidLeave && paidLeave.qtyOUT === 100)
    return { ...vars, vPresent: 1, vHrs: policy.dailyMaxRegHrs };

  const nightDiffStart = moment(ndStart, 'HH:mm'),
    nightDiffEnd = moment(ndEnd, 'HH:mm');

  /**
   * @type {holiday}
   */
  const holiday =
    holidays.find(
      item => item.ixDate === formatDateToInt(date) && Boolean(item.ixDIV)
    ) || holidays.find(item => item.ixDate === formatDateToInt(date));

  // const nextDay = formatDateToInt(
  //   moment(date)
  //     .add(1, 'day')
  //     .format('YYYY-MM-DD')
  // );

  // const holidayNextDay =
  //   holidays.find(item => item.ixDate === nextDay && Boolean(item.ixDIV)) ||
  //   holidays.find(item => item.ixDate === nextDay);

  const isRegularHoliday =
    holiday && holiday.ixHolidayType === HOLIDAY_TYPES.REGULAR_HOLIDAY;
  const isSpecialHoliday =
    holiday && holiday.ixHolidayType === HOLIDAY_TYPES.SPECIAL_HOLIDAY;

  // const isRegularHolidayTomorrow =
  //   holidayNextDay &&
  //   holidayNextDay.ixHolidayType === HOLIDAY_TYPES.REGULAR_HOLIDAY;

  // const isSpecialHolidayTomorrow =
  //   holidayNextDay &&
  //   holidayNextDay.ixHolidayType === HOLIDAY_TYPES.SPECIAL_HOLIDAY;

  if (otIn && otOut) {
    const moment_otIn = moment(formatTimeToStr(otIn), 'HH:mm');
    const moment_otOut = moment(formatTimeToStr(otOut), 'HH:mm');

    if (moment_otIn.isAfter(moment_otOut)) moment_otOut.add(1, 'day');

    let overtimeInMins = moment_otOut.diff(moment_otIn, 'minutes');
    if (overtimeInMins < policy.ot.minimum) overtimeInMins = 0;

    const ndMins = calculateNightDifferential(
      nightDiffStart,
      nightDiffEnd,
      moment_otIn,
      moment_otOut
    );

    //if the ND allow to have a OT
    const isNotAllow_vOT_ND = policy.nightDifferential?.isNotAllow_vOT_ND;

    if (isNotAllow_vOT_ND) {
      vars.vND += convertMinsToHours(
        ndMins,
        policy.nightDifferential.allowedRemainder
      );
    } else {
      vars.vOT_ND += convertMinsToHours(
        ndMins,
        policy.nightDifferential.allowedRemainder
      );
    }

    const otHrs = convertMinsToHours(
      overtimeInMins,
      policy.ot.allowedRemainder
    );

    if (isRestDay) vars.vOT_RD_OT = otHrs;
    else if (isRegularHoliday) vars.vOT_RH = otHrs;
    else if (isSpecialHoliday) vars.vOT_SH = otHrs;
    else vars.vOT = otHrs;

    if (policy.ot.fixedRate.allow) {
      const {
        rate1: otRate1,
        rate2: otRate2,
        variables: otVariables
      } = policy.ot.fixedRate;

      const isHolidayOrRestday =
        isRestDay || isSpecialHoliday || isRegularHoliday;

      if (!isHolidayOrRestday && otHrs >= 2)
        vars.valueOT = isString(otRate1)
          ? solveExpression(otRate1, {
              ...variables,
              ...otVariables
            })
          : otRate1;

      if (isHolidayOrRestday && otHrs >= 6) {
        vars.valueOT = isString(otRate2)
          ? solveExpression(otRate2, {
              ...variables,
              ...otVariables
            })
          : otRate2;
      }
    }
  }

  const { schedIn1, schedOut1, schedIn2, schedOut2 } = getSchedule({
    workHours,
    shifts
  });

  // Existing OBs
  const obAmInExists = hasOB(leaves, schedIn1);
  const obAmOutExists = hasOB(leaves, schedOut1);
  const obPmInExists = hasOB(leaves, schedIn2);
  const obPmOutExists = hasOB(leaves, schedOut2);

  const m_schedIn1 = moment(schedIn1, 'HH:mm');
  const m_schedOut1 = moment(schedOut1, 'HH:mm');
  if (m_schedOut1.isBefore(m_schedIn1)) m_schedOut1.add(1, 'day');

  const m_schedIn2 = moment(schedIn2, 'HH:mm');
  if (m_schedIn2.isBefore(m_schedOut1)) m_schedIn2.add(1, 'day');

  const m_schedOut2 = moment(schedOut2, 'HH:mm');
  if (m_schedOut2.isBefore(m_schedIn2)) m_schedOut2.add(1, 'day');

  let amTimeIn, amTimeOut, pmTimeIn, pmTimeOut;

  const { allow: allowOffset, max: maxOffset } = policy.late.offset;

  let late1 = 0,
    late2 = 0,
    amUT = 0,
    allowedBreakFrom,
    allowedBreakTo;

  if (policy.noNoonLogs) {
    if ((!in1 && !obAmInExists) || (!out2 && !obPmOutExists)) {
      if (isRestDay) minsRenderedOnRestDay -= dailyMaxRegMins;
      else if (prMode === 0) vars.vAbsent += 1;
    } else {
      const isHalfDay =
        policy.allowHalfDayShift &&
        m_schedOut2.diff(m_schedIn1, 'hours') % 24 === 4;

      amTimeIn = moment(in1 ? formatTimeToStr(in1) : schedIn1, 'HH:mm');
      const am_in_diff = isHalfDay
        ? moment(amTimeIn.format('HH:mm'), 'HH:mm').diff(
            moment(m_schedIn1.format('HH:mm'), 'HH:mm'),
            'minutes'
          )
        : amTimeIn.diff(m_schedIn1, 'minutes');

      if (am_in_diff > 0) {
        if (isRestDay) minsRenderedOnRestDay -= am_in_diff;
        else if (policy.late.gracePeriodMode === 1) {
          vars.vLate +=
            am_in_diff > policy.late.gracePeriod
              ? am_in_diff - policy.late.gracePeriod
              : 0;
        } else {
          vars.vLate += am_in_diff > policy.late.gracePeriod ? am_in_diff : 0;
          if (vars.vLate > policy.breakAfter) {
            const breakAfterDiff = vars.vLate - policy.breakAfter;
            vars.vLate -=
              policy.break > breakAfterDiff ? breakAfterDiff : policy.break;
          }
        }
      }

      pmTimeOut = moment(out2 ? formatTimeToStr(out2) : schedOut2, 'HH:mm');
      if (pmTimeOut.isBefore(amTimeIn)) pmTimeOut.add(1, 'day');

      const pm_out_diff = isHalfDay
        ? moment(m_schedOut2.format('HH:mm'), 'HH:mm').diff(
            moment(pmTimeOut.format('HH:mm'), 'HH:mm'),
            'minutes'
          )
        : m_schedOut2.diff(pmTimeOut, 'minutes');

      if (pm_out_diff > 0) {
        if (isRestDay) {
          minsRenderedOnRestDay -= pm_out_diff;
        } else {
          vars.vUT += pm_out_diff;
          if (vars.vUT > policy.breakAfter) {
            const breakAfterDiff = vars.vUT - policy.breakAfter;
            vars.vUT -=
              policy.break > breakAfterDiff ? breakAfterDiff : policy.break;
          }
        }
      }

      const ndMins = calculateNightDifferential(
        nightDiffStart,
        nightDiffEnd,
        am_in_diff > 0 ? amTimeIn : m_schedIn1,
        pm_out_diff > 0 ? pmTimeOut : m_schedOut2
      );

      nightDiffHrs += convertMinsToHours(
        ndMins,
        policy.nightDifferential.allowedRemainder
      );

      if (allowOffset && vars.vLate > 0 && maxOffset > 0) {
        let excess = pmTimeOut.diff(m_schedOut2, 'minutes');
        excess = excess > maxOffset ? maxOffset : excess;

        if (excess > 0) {
          vars.vLate = excess >= vars.vLate ? 0 : vars.vLate - excess;
        }
      }

      if (!isRestDay) {
        vars.vHrs += convertMinsToHours(
          policy.dailyMaxRegHrs * 60 - vars.vUT - vars.vLate
        );
        if (policy?.presentBasedOnHrs) {
          vars.vPresent = vars.vHrs / (policy?.dailyMaxRegHrs || 8);
        } else {
          vars.vPresent = isHalfDay ? 0.5 : 1;
        }
      }
    }
  } else {
    const hasNoAmOutIn = (!in1 && !obAmInExists) || (!out1 && !obAmOutExists);
    const hasNoPmOutIn = (!in2 && !obPmInExists) || (!out2 && !obPmOutExists);

    if (policy.allowedTimeOfBreak.allow) {
      allowedBreakFrom = moment(policy.allowedTimeOfBreak.from, 'HH:mm');
      allowedBreakTo = moment(policy.allowedTimeOfBreak.to, 'HH:mm');
    }

    vars.vPresent = !isRestDay && (!hasNoAmOutIn || !hasNoPmOutIn) ? 1 : 0;

    const minsDiffSched1 = m_schedOut1.diff(m_schedIn1, 'minutes');
    const minsDiffSched2 = m_schedOut2.diff(m_schedIn2, 'minutes');

    if (hasNoAmOutIn) {
      if (isRestDay) minsRenderedOnRestDay -= dailyMaxRegMins / 2;
      else if (prMode !== 0 && vars.vPresent) late1 += minsDiffSched1;
      else
        vars.vAbsent += round(minsDiffSched1 / 60 / policy.dailyMaxRegHrs, 4);
    } else {
      amTimeIn = moment(in1 ? formatTimeToStr(in1) : schedIn1, 'HH:mm');
      const am_in_diff = amTimeIn.diff(m_schedIn1, 'minutes');

      if (am_in_diff > 0) {
        if (isRestDay) minsRenderedOnRestDay -= am_in_diff;
        else late1 += am_in_diff;
      }

      amTimeOut = moment(out1 ? formatTimeToStr(out1) : schedOut1, 'HH:mm');
      if (amTimeOut.isBefore(amTimeIn)) amTimeOut.add(1, 'day');

      const am_out_diff = policy.allowedTimeOfBreak.allow
        ? allowedBreakFrom.diff(amTimeOut, 'minutes')
        : m_schedOut1.diff(amTimeOut, 'minutes');

      if (am_out_diff > 0) {
        if (isRestDay) minsRenderedOnRestDay -= am_out_diff;
        else {
          vars.vUT += am_out_diff;
          amUT += am_out_diff;
        }
      }

      const ndMins = calculateNightDifferential(
        nightDiffStart,
        nightDiffEnd,
        am_in_diff > 0 ? amTimeIn : m_schedIn1,
        am_out_diff > 0 ? amTimeOut : m_schedOut1
      );

      nightDiffHrs += convertMinsToHours(
        ndMins,
        policy.nightDifferential.allowedRemainder
      );
    }

    if (hasNoPmOutIn) {
      if (isRestDay) minsRenderedOnRestDay -= dailyMaxRegMins / 2;
      else if (prMode !== 0 && vars.vPresent) vars.vUT += minsDiffSched2;
      else
        vars.vAbsent += round(minsDiffSched2 / 60 / policy.dailyMaxRegHrs, 4);
    } else {
      pmTimeIn = moment(in2 ? formatTimeToStr(in2) : schedIn2, 'HH:mm');
      if (amTimeOut && pmTimeIn.isBefore(amTimeOut)) pmTimeIn.add(1, 'day');

      pmTimeOut = moment(out2 ? formatTimeToStr(out2) : schedOut2, 'HH:mm');
      if (pmTimeOut.isBefore(pmTimeIn)) pmTimeOut.add(1, 'day');

      let pm_in_diff = policy.allowedTimeOfBreak.allow
        ? pmTimeIn.diff(allowedBreakTo, 'minutes')
        : pmTimeIn.diff(m_schedIn2, 'minutes');

      if (out1 || obAmOutExists) {
        const amTimeOut = moment(
          out1 ? formatTimeToStr(out1) : schedOut1,
          'HH:mm'
        );

        const actualBreak = m_schedIn2.diff(m_schedOut1, 'minutes');

        // get the difference of actual out minus scheduled out of morning
        // to be deducted to the computed late of afternoon in
        const am_excess =
          policy.allowedTimeOfBreak.allow || actualBreak > policy.break
            ? 0
            : amTimeOut.diff(m_schedOut1, 'minutes');

        if (am_excess > 0) pm_in_diff -= am_excess;
      }

      if (pm_in_diff > 0) {
        if (isRestDay) {
          minsRenderedOnRestDay -= pm_in_diff;
        } else if (policy.late.pmGracePeriodMode === 1) {
          late2 +=
            pm_in_diff > policy.late.pmGracePeriod
              ? pm_in_diff - policy.late.pmGracePeriod
              : 0;
        } else {
          late2 += pm_in_diff > policy.late.pmGracePeriod ? pm_in_diff : 0;
        }
      }

      const pm_out_diff = m_schedOut2.diff(pmTimeOut, 'minutes');

      if (pm_out_diff > 0) {
        if (isRestDay) minsRenderedOnRestDay -= pm_out_diff;
        else vars.vUT += pm_out_diff;
      }

      const ndMins = calculateNightDifferential(
        nightDiffStart,
        nightDiffEnd,
        pm_in_diff > 0 ? pmTimeIn : m_schedIn2,
        pm_out_diff > 0 ? pmTimeOut : m_schedOut2
      );

      nightDiffHrs += convertMinsToHours(
        ndMins,
        policy.nightDifferential.allowedRemainder
      );

      if (allowOffset && maxOffset > 0 && late1 > 0) {
        let excess = pmTimeOut.diff(m_schedOut2, 'minutes');
        excess = excess > maxOffset ? maxOffset : excess;

        if (excess > 0) {
          late1 = excess >= late1 ? 0 : late1 - excess;
        }
      }
    }

    if (policy.allowedTimeOfBreak.allow && pmTimeIn && amTimeOut) {
      const pmTimeInAndAmTimeOutDiff = pmTimeIn.diff(amTimeOut, 'minutes');

      // if undertime and has afternoon late
      if (amUT > 0 && late2 > 0) {
        late2 +=
          allowedBreakTo.diff(allowedBreakFrom, 'minutes') - policy.break;
      }
      // if not undertime and no afternoon late
      // but diff of pm time in and am time out is greater than break
      else if (
        amUT === 0 &&
        late2 === 0 &&
        pmTimeInAndAmTimeOutDiff > policy.break
      ) {
        late2 += pmTimeInAndAmTimeOutDiff - policy.break;
      }
      // if undertime and no afternoon late
      else if (amUT > 0 && late2 === 0) {
        const pmInDiffBreakStartAndBreak =
          pmTimeIn.diff(allowedBreakFrom, 'minutes') - policy.break;

        if (pmInDiffBreakStartAndBreak > 0) late2 += pmInDiffBreakStartAndBreak;
      }
    }

    if (policy.late.gracePeriodMode === 1) {
      late1 =
        late1 > policy.late.gracePeriod ? late1 - policy.late.gracePeriod : 0;
    } else {
      late1 = late1 > policy.late.gracePeriod ? late1 : 0;
    }

    vars.vLate = late1 + late2;

    if (!isRestDay && (!hasNoAmOutIn || !hasNoPmOutIn)) {
      if (m_schedOut1.isBefore(m_schedIn1)) m_schedOut1.add(1, 'day');
      if (m_schedOut2.isBefore(m_schedIn2)) m_schedOut2.add(1, 'day');

      const dailyRegMins =
        m_schedOut1.diff(m_schedIn1, 'minutes') +
        m_schedOut2.diff(m_schedIn2, 'minutes');

      const minsRendered =
        dailyRegMins -
        vars.vAbsent * policy.dailyMaxRegHrs * 60 -
        vars.vLate -
        vars.vUT;

      vars.vHrs += round(minsRendered / 60, 4);
    }
  }

  const hoursRenderedOnRestDay = round(minsRenderedOnRestDay / 60, 4);

  if (isRegularHoliday) {
    // if payroll mode is daily
    if (prMode === 1) {
      if (policy.prorateRh) {
        const vRH_less = bioExempt
          ? 0
          : vars.vAbsent + round((vars.vLate + vars.vUT) / dailyMaxRegMins, 4);
        vars.vRH += 1 - vRH_less;
      } else vars.vRH = 1;

      if (isRestDay) {
        vars.vOT_RD_RH += hoursRenderedOnRestDay;
        vars.vND_RD_RH += nightDiffHrs;
      } else {
        vars.vND_RH += nightDiffHrs;
      }
    }

    // if rest day
    else if (isRestDay) {
      if (policy.prorateRh) {
        const vRH_less = bioExempt
          ? 0
          : vars.vAbsent + round((vars.vLate + vars.vUT) / dailyMaxRegMins, 4);
        vars.vRH += 1 - vRH_less;
      } else vars.vRH += 1 - vars.vAbsent;

      vars.vOT_RD_RH += hoursRenderedOnRestDay;
      vars.vND_RD_RH += nightDiffHrs;
    } else {
      if (policy.prorateRh) {
        const vRH_less = bioExempt
          ? 0
          : vars.vAbsent + round((vars.vLate + vars.vUT) / dailyMaxRegMins, 4);
        vars.vRH += 1 - vRH_less;
      } else vars.vRH += 1 - vars.vAbsent;

      vars.vND_RH += nightDiffHrs;

      if (!policy.prorateRh) vars.vAbsent = 0;
    }
  } else if (isSpecialHoliday) {
    if (isRestDay) {
      vars.vOT_RD_SH += hoursRenderedOnRestDay;
      vars.vND_RD_SH += nightDiffHrs;
    } else {
      const vSH_less = bioExempt
        ? 0
        : prMode === PR_MODES.STANDARD
        ? vars.vAbsent
        : vars.vPresent
        ? 0
        : 1;

      vars.vSH += 1 - vSH_less;
      vars.vND_SH += nightDiffHrs;

      if (policy.noAbsentOnSh) {
        vars.vAbsent = 0;
        vars.vLate = 0;
        vars.vUT = 0;
      }
    }
  } else if (isRestDay) {
    vars.vOT_RD += bioExempt ? 0 : hoursRenderedOnRestDay;
    vars.vND_RD += nightDiffHrs;
  } else {
    vars.vND += nightDiffHrs;
  }

  if (prMode === 2) {
    vars.vLate = 0;
    vars.vUT = 0;
  }

  if (paidLeave && paidLeave !== 0) vars.vAbsent -= paidLeave.qtyOUT / 100;
  if (vars.vAbsent > 1) vars.vAbsent = 1;

  if (policy.late.fixedRate.allow && policy.late.fixedRate.storeTo) {
    vars[policy.late.fixedRate.storeTo] =
      late1 > policy.late.fixedRate.max ? policy.late.fixedRate.max : late1;

    vars.vLate =
      late1 > policy.late.fixedRate.max
        ? late1 + late2 - policy.late.fixedRate.max
        : late2;
  }

  if (vars.vSH && !bioExempt && (vars.vUT || vars.vLate)) {
    vars.vSH -= round((vars.vUT + vars.vLate) / dailyMaxRegMins, 4);
  }

  for (const varKey of Object.keys(vars)) {
    if (!vars[varKey] || vars[varKey] < 0) vars[varKey] = 0;
  }

  if (bioExempt && !isRestDay) {
    vars.vAbsent = 0;
    vars.vLate = 0;
    vars.vUT = 0;
    vars.vHrs = policy.dailyMaxRegHrs;
    vars.vPresent = 1;
  }

  return vars;
}

export default computeTimeLogs;
